"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useMetricsDraftSchema = void 0;
var react_1 = require("react");
var metrics_utils_1 = require("../utils/metrics-utils");
var metrics_change_detection_1 = require("../utils/metrics-change-detection");
/**
 * Encapsulates the saved/draft schema state pattern shared by all
 * AI-Hub admin "categorized items" screens (Metrics today, Personas
 * next). Includes save/discard semantics and per-category /
 * per-property change detection.
 */
exports.useMetricsDraftSchema = function (initialSchema) {
    var _a = react_1.useState(function () {
        return metrics_utils_1.cloneMetricsSchema(initialSchema);
    }), savedSchema = _a[0], setSavedSchema = _a[1];
    var _b = react_1.useState(function () {
        return metrics_utils_1.cloneMetricsSchema(initialSchema);
    }), draftSchema = _b[0], setDraftSchema = _b[1];
    // Re-sync local state when the parent supplies a new authoritative
    // `initialSchema` (e.g. after the API fetch resolves or after a
    // forced refresh). Without this, `useState` would keep its first-
    // render snapshot forever and silently drop server-side data —
    // which is exactly why predefined groups vanished after refresh
    // while locally-added ones survived.
    //
    // We compare references; the parent rebuilds the schema on each
    // fetch, so a new array signals a server refresh. When the user
    // has unsaved local edits we leave `draftSchema` alone so they
    // don't lose work, but we still rebase `savedSchema` so the diff
    // is computed against the fresh server snapshot.
    var previousInitialRef = react_1.useRef(initialSchema);
    react_1.useEffect(function () {
        if (initialSchema === previousInitialRef.current) {
            return;
        }
        previousInitialRef.current = initialSchema;
        var rebuilt = metrics_utils_1.cloneMetricsSchema(initialSchema);
        setSavedSchema(rebuilt);
        // Only overwrite the draft when there are no pending edits to
        // protect, otherwise the user would silently lose their changes.
        setDraftSchema(function (current) {
            return metrics_utils_1.areMetricsSchemasEqual(current, savedSchema) ? metrics_utils_1.cloneMetricsSchema(rebuilt) : current;
        });
    }, [initialSchema, savedSchema]);
    var hasChanges = react_1.useMemo(function () { return !metrics_utils_1.areMetricsSchemasEqual(draftSchema, savedSchema); }, [draftSchema, savedSchema]);
    var savedPropertyIndex = react_1.useMemo(function () { return metrics_change_detection_1.buildSavedPropertyIndex(savedSchema); }, [savedSchema]);
    var updateDraft = react_1.useCallback(function (updater) {
        setDraftSchema(function (current) { return updater(metrics_utils_1.cloneMetricsSchema(current)); });
    }, []);
    var saveChanges = react_1.useCallback(function () {
        setDraftSchema(function (current) {
            var committed = metrics_utils_1.cloneMetricsSchema(current);
            setSavedSchema(committed);
            return metrics_utils_1.cloneMetricsSchema(committed);
        });
    }, []);
    var discardChanges = react_1.useCallback(function () {
        setDraftSchema(metrics_utils_1.cloneMetricsSchema(savedSchema));
    }, [savedSchema]);
    var isCategoryChanged = react_1.useCallback(function (categoryId) { return metrics_change_detection_1.isCategoryChanged(categoryId, draftSchema, savedSchema); }, [draftSchema, savedSchema]);
    var isPropertyChanged = react_1.useCallback(function (property, categoryId) {
        return metrics_change_detection_1.isPropertyChanged(property, categoryId, savedPropertyIndex);
    }, [savedPropertyIndex]);
    var findCategory = react_1.useCallback(function (categoryId) {
        return draftSchema.find(function (category) { return category.id === categoryId; }) || null;
    }, [draftSchema]);
    return {
        savedSchema: savedSchema,
        draftSchema: draftSchema,
        hasChanges: hasChanges,
        updateDraft: updateDraft,
        saveChanges: saveChanges,
        discardChanges: discardChanges,
        savedPropertyIndex: savedPropertyIndex,
        isCategoryChanged: isCategoryChanged,
        isPropertyChanged: isPropertyChanged,
        findCategory: findCategory,
    };
};
